//import React, { useState, useEffect } from 'react';
import React, { useState, useEffect, useMemo } from "react";
import axios from 'axios';
import Paper from '@mui/material/Paper';
import SearchIcon from '@mui/icons-material/Search';
import * as XLSX from 'xlsx';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { IconButton } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { Link } from 'react-router-dom'; // Import Link from react-router-dom
import Button from '@mui/material/Button';
import RefreshIcon from '@mui/icons-material/Refresh';
import FeedIcon from '@mui/icons-material/Feed';
import Select from 'react-select'; // Import react-select
import {
    Checkbox, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TableSortLabel, TablePagination, TableFooter
} from '@mui/material';
import { Tooltip } from '@mui/material';
import { Download, RefreshCw } from "lucide-react";
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';


function SalePaymentWise() {
    const [dataBill, setDataBill] = useState([]);
    const [dataItem, setDataItem] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);
    const tableRef = React.createRef();
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [financialDate, setFinancialDate] = useState('');
    const [selectedOption, setSelectedOption] = useState('itemWise');
    const [customerId, setCustomer] = useState('');
    const { isAuthenticated, companyId, permissions, token, username, logout } = useAuth();
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        setFromDate(getFormattedCurrentDate()); // Set to current date instead of financial year start
        setToDate(getFormattedCurrentDate());
        setFinancialDate(getFormattedStartFinancialDate());
    }, [isAuthenticated, companyId, token, logout]);

    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };

    const getFormattedStartDate = () => {
        const today = new Date();
        let year = today.getFullYear();

        if (today.getMonth() + 1 > 3) {
            // If the current month is greater than 3, use the current year
            return `${year}-04-01`;
        } else {
            // If the current month is 3 or less, use the previous year
            return `${year - 1}-04-01`;
        }
    };

    const getFormattedStartFinancialDate = () => {
        const monthStartFinancialDate = new Date();
        monthStartFinancialDate.setDate(1);
        return monthStartFinancialDate.toISOString().split('T')[0];
    };

    const handleFromDateChange = (e) => {
        const selectedDate = e.target.value;
        // Add validation if needed
        setFromDate(selectedDate);
    };

    const handleFinancialDateChange = (e) => {
        const selectedDate = e.target.value;
        // Add validation if needed
        setFinancialDate(selectedDate);
    };

    // Similar function for toDate
    const handleToDateChange = (e) => {
        const selectedDate = e.target.value;
        // Add validation if needed
        setToDate(selectedDate);
    };

    const hasReadReportPermission = permissions.includes('Read Reports');

    const handleChange = (event) => {
        setSelectedOption(event.target.value);
    };

    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    const toggleSearchInput = () => {
        setShowSearchInput(!showSearchInput);
    };

    const filteredDataBill = dataBill.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const filteredDataItem = dataItem.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        return new Date(dateString).toLocaleDateString('en-GB', options);
    };

    const [isLoading, setIsLoading] = useState(false);

    // const filteredDataItem = useMemo(() => {
    //     return dataItem;
    // }, [dataItem]);

    const fetchTableData = async () => {
        setDataItem([]);
        setIsLoading(true);
        try {
            const response = await fetchData('ReportApi', `/SalePaymentWise/${fromDate}/${toDate}/${companyId}`, token);
            if (response.responseCode === "1111") {
                setDataItem(response.response_data);
            } else {
                console.warn("Unexpected response code:", response.responseCode);
            }
        } catch (error) {
            console.error("Error fetching Trial Balance Details:", error.message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchTableData();
    }, []);



   const handleExportExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sale PaymentWise Details');

    const now = new Date();
    const formattedDateTime = `${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;

    // Header caption
    worksheet.mergeCells('A1:F1');
    const captionCell = worksheet.getCell('A1');
    captionCell.value = `Sale PaymentWise Data From : ${formatDate(fromDate)} To : ${formatDate(toDate)} | Generated On: ${formattedDateTime} By : ${username}`;
    captionCell.alignment = { horizontal: 'center' };
    captionCell.font = { bold: true, size: 12 };
    captionCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFD9D9D9' } // Light gray
    };

    // Column headers
    const headerRow = worksheet.addRow(['Bill Date', 'Grand Total', 'Cash Amount', 'Card Amount', 'UPI Amount', 'Credit Amount']);
    headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } }; // White text
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4F81BD' } }; // Blue background
        cell.alignment = { horizontal: 'center' };
        cell.border = {
            top: { style: 'thin' },
            bottom: { style: 'thin' },
            left: { style: 'thin' },
            right: { style: 'thin' }
        };
    });

    // Data rows
    filteredDataItem.forEach((rowData) => {
        const row = worksheet.addRow([
            rowData.BillDate ? formatDate(rowData.BillDate) : '',
            parseFloat(rowData.GrandTotal) || 0,
            parseFloat(rowData.CashAmount) || 0,
            parseFloat(rowData.CardAmount) || 0,
            parseFloat(rowData.UPIAmount) || 0,
            parseFloat(rowData.CreditAmoount) || 0,
        ]);

        row.eachCell((cell) => {
            cell.border = {
                top: { style: 'thin' },
                bottom: { style: 'thin' },
                left: { style: 'thin' },
                right: { style: 'thin' }
            };
            if (typeof cell.value === "number") {
                cell.numFmt = "#,##0.00"; // Format numeric cells
                cell.alignment = { horizontal: 'right' };
            } else {
                cell.alignment = { horizontal: 'left' };
            }
        });
    });

    // ---- Calculate Totals ----
    const totalGrand = filteredDataItem.reduce((acc, item) => acc + (parseFloat(item.GrandTotal) || 0), 0);
    const totalCash = filteredDataItem.reduce((acc, item) => acc + (parseFloat(item.CashAmount) || 0), 0);
    const totalCard = filteredDataItem.reduce((acc, item) => acc + (parseFloat(item.CardAmount) || 0), 0);
    const totalUPI = filteredDataItem.reduce((acc, item) => acc + (parseFloat(item.UPIAmount) || 0), 0);
    const totalCredit = filteredDataItem.reduce((acc, item) => acc + (parseFloat(item.CreditAmoount) || 0), 0);

    // ---- Add Total Row ----
    const totalRow = worksheet.addRow([
        'Total',
        totalGrand,
        totalCash,
        totalCard,
        totalUPI,
        totalCredit,
    ]);

    totalRow.eachCell((cell, colNumber) => {
        cell.font = { bold: true };
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFFFE599' } // Light yellow background
        };
        cell.border = {
            top: { style: 'thin' },
            bottom: { style: 'thin' },
            left: { style: 'thin' },
            right: { style: 'thin' }
        };
        if (colNumber > 1) {
            cell.numFmt = "#,##0.00";
            cell.alignment = { horizontal: 'right' };
        } else {
            cell.alignment = { horizontal: 'left' };
        }
    });

    // Adjust column widths
    worksheet.columns.forEach((col) => (col.width = 20));

    // Save file
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), 'Sale_Paymentwise.xlsx');
};



    const [page, setPage] = useState(0); // Current page
    const [rowsPerPage, setRowsPerPage] = useState(15); // Rows per page

    // Define initial column order
    const [columnOrder, setColumnOrder] = useState([
        'Bill Date',
        'Grand Total',
        'Cash Amount',
        'Card Amount',
        'UPI Amount',
        'Credit Amount',

    ]);

    // Handle page change
    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    // Handle rows per page change
    const handleRowsPerPageChange = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0); // Reset to the first page
    };

    // Function to handle drag start
    const handleDragStart = (e, index) => {
        e.dataTransfer.setData('columnIndex', index);
    };

    // Function to handle drag over
    const handleDragOver = (e) => {
        e.preventDefault(); // Allow drop
    };

    // Function to handle drop
    const handleDrop = (e, index) => {
        const draggedIndex = e.dataTransfer.getData('columnIndex');
        if (draggedIndex !== index) {
            const newOrder = [...columnOrder];
            // Swap columns
            [newOrder[draggedIndex], newOrder[index]] = [newOrder[index], newOrder[draggedIndex]];
            setColumnOrder(newOrder);
        }
    };

    // Process and paginate the data
    const processedData = filteredDataItem.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );



    // KPI values
    const monthlyTotal = processedData.reduce((a, b) => a + (b.monthlyavg || 0), 0);
    const quarterlyTotal = processedData.reduce((a, b) => a + (b.quarterlyavg || 0), 0);
    const halfyearTotal = processedData.reduce((a, b) => a + (b.halfyearavg || 0), 0);
    const yearlyTotal = processedData.reduce((a, b) => a + (b.yearlyavg || 0), 0);

    return (
        <div className="w-full mx-auto" style={{ width: "100%", marginTop: "-1%" }}>
            {/* Header Row */}
            <div
                style={{
                    background: "whitesmoke",
                    color: "black",
                    padding: "5px 10px",
                    borderTopLeftRadius: "6px",
                    borderTopRightRadius: "6px",
                    fontSize: "12px",
                    fontWeight: "bold",
                    letterSpacing: "0.5px",
                    marginTop: '-30px',
                    height: '30px'
                }}
            >
                DAILY SETTLEMENT REPORT
            </div>

            {/* ===== Filter Section ===== */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px 15px",
                    borderBottom: "1px solid #eee",
                    background: "#fafafa",
                }}
            >
                {/* Search */}
                <input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={handleSearch}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    style={{
                        width: isFocused ? "260px" : "200px",
                        height: "30px",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        padding: "0 10px",
                        transition: "width 0.3s",
                        outline: "none",
                        fontSize: "13px",
                    }}
                />

                {/* Dates + Buttons */}
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    {/* From Date */}
                    <div>
                        <label style={{ fontSize: "11px", color: "#555" }}>From</label>
                        <input
                            type="date"
                            value={fromDate}
                            onChange={handleFromDateChange}
                            style={{
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                padding: "2px 6px",
                                fontSize: "12px",
                                height: "28px",
                                marginLeft: "5px",
                            }}
                        />
                    </div>

                    {/* To Date */}
                    <div>
                        <label style={{ fontSize: "11px", color: "#555" }}>To</label>
                        <input
                            type="date"
                            value={toDate}
                            onChange={handleToDateChange}
                            style={{
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                padding: "2px 6px",
                                fontSize: "12px",
                                height: "28px",
                                marginLeft: "5px",
                            }}
                        />
                    </div>

                    {/* Refresh */}
                    <Button
                        onClick={fetchTableData}
                        style={{
                            background: "#2f4050",
                            color: "white",
                            borderRadius: "4px",
                            padding: "5px 12px",
                            fontSize: "13px",
                            fontWeight: "bold",
                            height: "28px",
                        }}
                    >
                        Refresh
                    </Button>

                    {/* Excel */}
                    <Button
                        onClick={handleExportExcel}
                        style={{
                            background: "#00695c",
                            color: "white",
                            borderRadius: "4px",
                            padding: "5px 12px",
                            fontSize: "13px",
                            fontWeight: "bold",
                            height: "28px",
                        }}
                    >
                        Excel
                    </Button>
                </div>
            </div>

            {/* Table */}
            {/* Table */}
            <div
                className="w-full mb-5 flex"
                style={{
                    maxHeight: "540px",
                    overflowY: "auto",
                    marginTop: "-0.50%",
                    border: "1px solid #e0e0e0",
                    borderRadius: "6px",
                }}
            >
                <TableContainer>
                    <Table stickyHeader size="small">
                        {/* Header */}
                        <TableHead>
                            <TableRow>
                                {columnOrder.map((col, index) => (
                                    <TableCell
                                        key={index}
                                        style={{
                                            background: "#E6EAF2", // 🌸 soft pastel blue-gray
                                            color: "#333",
                                            fontWeight: 600,
                                            fontSize: "11.5px",
                                            padding: "6px",
                                            borderBottom: "2px solid #d1d5db",
                                            textAlign: col === "Bill Date" ? "left" : "right",
                                            textTransform: "uppercase",
                                        }}
                                    >
                                        {col}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        {/* Body */}
                        <TableBody>
                            {processedData
                                .filter((rowData) => rowData.BillDate !== "Total")
                                .map((rowData, index) => {
                                    const rowValues = {
                                        "Bill Date": rowData.BillDate ? formatDate(rowData.BillDate) : "",
                                        "Grand Total": parseFloat(rowData.GrandTotal || 0).toFixed(2),
                                        "Cash Amount": parseFloat(rowData.CashAmount || 0).toFixed(2),
                                        "Card Amount": parseFloat(rowData.CardAmount || 0).toFixed(2),
                                        "UPI Amount": parseFloat(rowData.UPIAmount || 0).toFixed(2),
                                        "Credit Amount": parseFloat(rowData.CreditAmoount || 0).toFixed(2),
                                    };

                                    return (
                                        <TableRow
                                            key={rowData.Id || index}
                                            sx={{
                                                backgroundColor: index % 2 === 0 ? "#FFFFFF" : "#F8FAFC", // 🩵 subtle alternating pastel
                                                "&:hover": {
                                                    backgroundColor: "#EDF2F7", // soft hover effect
                                                    cursor: "pointer",
                                                },
                                            }}
                                        >
                                            {columnOrder.map((col, idx) => (
                                                <TableCell
                                                    key={idx}
                                                    style={{
                                                        padding: "6px",
                                                        fontSize: "12.5px",
                                                        borderBottom: "1px solid #e5e7eb",
                                                        textAlign: col === "Bill Date" ? "left" : "right",
                                                        color: "#374151",
                                                    }}
                                                >
                                                    {rowValues[col]}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    );
                                })}

                            {/* ✅ Total Row */}
                            {(() => {
                                if (processedData.length === 0) return null;

                                const totals = processedData
                                    .filter((r) => r.BillDate !== "Total")
                                    .reduce(
                                        (acc, curr) => ({
                                            GrandTotal: acc.GrandTotal + (parseFloat(curr.GrandTotal) || 0),
                                            CashAmount: acc.CashAmount + (parseFloat(curr.CashAmount) || 0),
                                            CardAmount: acc.CardAmount + (parseFloat(curr.CardAmount) || 0),
                                            UPIAmount: acc.UPIAmount + (parseFloat(curr.UPIAmount) || 0),
                                            CreditAmoount:
                                                acc.CreditAmoount + (parseFloat(curr.CreditAmoount) || 0),
                                        }),
                                        {
                                            GrandTotal: 0,
                                            CashAmount: 0,
                                            CardAmount: 0,
                                            UPIAmount: 0,
                                            CreditAmoount: 0,
                                        }
                                    );

                                return (
                                    <TableRow
                                        sx={{
                                            backgroundColor: "#DDE8D9", // 💚 pastel green for totals
                                            position: "sticky",
                                            bottom: 0,
                                        }}
                                    >
                                        <TableCell
                                            style={{
                                                padding: "6px",
                                                fontSize: "13px",
                                                fontWeight: "bold",
                                                textAlign: "left",
                                                color: "#2F4F4F",
                                            }}
                                        >
                                            TOTAL
                                        </TableCell>

                                        <TableCell style={{ textAlign: "right", fontWeight: "bold", color: "#2F4F4F" }}>
                                            {totals.GrandTotal.toFixed(2)}
                                        </TableCell>
                                        <TableCell style={{ textAlign: "right", fontWeight: "bold", color: "#2F4F4F" }}>
                                            {totals.CashAmount.toFixed(2)}
                                        </TableCell>
                                        <TableCell style={{ textAlign: "right", fontWeight: "bold", color: "#2F4F4F" }}>
                                            {totals.CardAmount.toFixed(2)}
                                        </TableCell>
                                        <TableCell style={{ textAlign: "right", fontWeight: "bold", color: "#2F4F4F" }}>
                                            {totals.UPIAmount.toFixed(2)}
                                        </TableCell>
                                        <TableCell style={{ textAlign: "right", fontWeight: "bold", color: "#2F4F4F" }}>
                                            {totals.CreditAmoount.toFixed(2)}
                                        </TableCell>
                                    </TableRow>
                                );
                            })()}
                        </TableBody>



                        {/* Footer */}
                        <TableFooter>
                            <TableRow>
                                <TablePagination
                                    rowsPerPageOptions={[15, 25, 50, 100]}
                                    count={filteredDataItem.length}
                                    rowsPerPage={rowsPerPage}
                                    page={page}
                                    onPageChange={handlePageChange}
                                    onRowsPerPageChange={handleRowsPerPageChange}
                                />
                            </TableRow>
                        </TableFooter>
                    </Table>
                </TableContainer>
            </div>
        </div>
    );
}

function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}


export default SalePaymentWise;